/**
 * Core runtime mode + debug gating.
 *
 * Env contract:
 * - VITE_CHAT_RUNTIME_MODE: optional override, "development" | "production"
 * - VITE_CHAT_DEBUG: optional debug switch, "1" | "true" enables debug logs
 *
 * Safety rule:
 * - Debug logs are allowed only when runtime mode is non-production.
 */

function toBooleanFlag(value) {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on';
}

function normalizeMode(value) {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'production' || normalized === 'prod') {
    return 'production';
  }
  if (normalized === 'development' || normalized === 'dev') {
    return 'development';
  }
  return '';
}

function runtimeEnvironment(envInput) {
  return (
    envInput
    && typeof envInput === 'object'
    && !Array.isArray(envInput)
  ) ? envInput : (import.meta?.env || {});
}

export function resolveRuntimeMode(envInput) {
  const env = runtimeEnvironment(envInput);
  const overrideMode = normalizeMode(env.VITE_CHAT_RUNTIME_MODE);
  const modeFromVite = normalizeMode(env.MODE);
  const fallbackMode = env.PROD === true ? 'production' : 'development';
  const mode = overrideMode || modeFromVite || fallbackMode;
  const isProduction = mode === 'production';
  const isDevelopment = !isProduction;
  const debugRequested = toBooleanFlag(env.VITE_CHAT_DEBUG);
  const debugEnabled = isDevelopment && debugRequested;

  return {
    mode,
    isProduction,
    isDevelopment,
    debugEnabled,
  };
}

const runtimeMode = resolveRuntimeMode();

export function getCoreRuntimeMode() {
  return runtimeMode;
}

export function isCoreDebugEnabled() {
  return runtimeMode.debugEnabled;
}

export function debugCoreLog(...args) {
  if (!runtimeMode.debugEnabled) {
    return;
  }
  globalThis.console?.debug('[flare-core:debug]', ...args);
}

export default getCoreRuntimeMode;
